/**
 * Gate 1 — phantom references.
 *
 * Flags entity fields whose declared type is a PascalCase identifier that
 * resolves to no entity, no enum, and no known scalar. This is the gate that
 * would have caught the phantom entity names in doc 129 §8.
 *
 * @see docs/impl/129-data-model-coverage-audit.html §21
 */
import type { AtlasManifest } from '../types.js';
import { baseTypeName } from '../parser/resolveCrossRefs.js';
import type { ValidationIssue, ValidatorOptions } from './index.js';

/** PascalCase types that are primitive-ish and never need a schema. */
const DEFAULT_KNOWN_SCALARS = new Set([
  'Date',
  'Timestamp',
  'GeoPoint',
  'Buffer',
  'Promise',
  'Array',
  'ReadonlyArray',
  'Record',
  'Partial',
  'Readonly',
  'Pick',
  'Omit',
]);

const PASCAL_CASE = /^[A-Z]/;

/** Detect entity fields that reference an undefined PascalCase type. */
export function phantomRefs(
  manifest: AtlasManifest,
  options: ValidatorOptions = {},
): ValidationIssue[] {
  const entityIds = new Set(manifest.entities.map((e) => e.id));
  const knownScalars = new Set(DEFAULT_KNOWN_SCALARS);
  for (const s of options.knownScalars ?? []) knownScalars.add(s);

  const issues: ValidationIssue[] = [];
  for (const entity of manifest.entities) {
    for (const field of entity.fields) {
      // Already resolved by the cross-ref pass — not a phantom.
      if (field.referencesEntity || field.referencesEnum) continue;

      const base = baseTypeName(field.type);
      if (!base || !PASCAL_CASE.test(base)) continue;
      if (entityIds.has(base) || knownScalars.has(base)) continue;

      issues.push({
        gate: 'phantom-ref',
        ref: `${entity.id}.${field.name}`,
        message: `Field "${entity.id}.${field.name}" references unknown type "${base}" — no entity, enum, or known scalar declares it.`,
      });
    }
  }
  return issues;
}
